import { useMemo, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, Upload, ArrowLeft, ArrowRight, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { apiList, apiPost } from "@/integrations/api/client";
import {
  IMPORT_COLUMNS, downloadTemplate, parseCsvFile, validateRows,
  downloadErrorReport, downloadCodesList,
  type ImportRow, type Lookups, type ValidatedRow,
} from "@/features/assets/importCsv";
import { ASSET_STATUSES, CRITICALITIES, FUNDING_SOURCES } from "@/features/assets/types";

export const Route = createFileRoute("/_authenticated/assets/import")({
  component: ImportPage,
});

const ALLOWED = new Set(["super_admin", "biomed_head", "facility_manager"]);

function ImportPage() {
  const navigate = useNavigate();
  const { data: me, isLoading: meLoading } = useCurrentUser();
  const canImport = (me?.roles ?? []).some((r) => ALLOWED.has(r));

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [, setRows] = useState<ImportRow[]>([]);
  const [validated, setValidated] = useState<ValidatedRow[]>([]);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [summary, setSummary] = useState<{ imported: string[]; failed: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validRows = useMemo(() => validated.filter((v) => v.errors.length === 0), [validated]);
  const errorRows = useMemo(() => validated.filter((v) => v.errors.length > 0), [validated]);

  async function handleFile(file: File) {
    setParsing(true);
    try {
      const parsed = await parseCsvFile(file);
      setRows(parsed);
      
      const [cats, locs, depts, vens, serials, users, stores] = await Promise.all([
        apiList<{ id: string; name: string; parent_id: string | null }>("/api/v1/masters/asset-categories/", { limit: 1000 }),
        apiList<{ id: string; name: string; branch_id: string }>("/api/v1/locations/locations/", { limit: 1000 }),
        apiList<{ id: string; name: string }>("/api/v1/accounts/departments/", { limit: 1000 }),
        apiList<{ id: string; name: string }>("/api/v1/masters/vendors/", { limit: 1000 }),
        apiList<{ serial_number: string }>("/api/v1/assets/", { limit: 10000, ordering: "-created_at" }),
        apiList<{ id: string; email: string }>("/api/v1/accounts/admin-users/", { limit: 1000 }),
        apiList<{ id: string; name: string; branch_id: string }>("/api/v1/locations/stores/", { limit: 1000 }),
      ]);

      const lookups: Lookups = {
        categories: cats.results ?? [],
        locations: locs.results ?? [],
        departments: depts.results ?? [],
        vendors: vens.results ?? [],
        users: users.results ?? [],
        stores: stores.results ?? [],
      };
      const existing = new Set(
        (serials.results ?? [])
          .map((r) => (r.serial_number ? String(r.serial_number).toLowerCase() : null))
          .filter((s): s is string => !!s),
      );
      setValidated(validateRows(parsed, lookups, existing));
      setStep(2);
    } catch (e: unknown) {
      toast.error((e as Error).message || "Failed to parse file");
    } finally {
      setParsing(false);
    }
  }

  async function runImport() {
    setImporting(true);
    const codes: string[] = [];
    let failed = 0;
    for (const v of validRows) {
      try {
        const created = await apiPost<{ asset_code?: string }>("/api/v1/assets/", v.payload);
        if (created?.asset_code) codes.push(created.asset_code);
      } catch (e) {
        console.error("Import failed for row", v.rowIndex, e);
        failed++;
      }
    }
    setSummary({ imported: codes, failed });
    setImporting(false);
    toast.success(`Imported ${codes.length} assets`);
  }

  if (meLoading) return <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!canImport) return (
    <Card className="p-8 text-center">
      <AlertCircle className="mx-auto h-8 w-8 text-amber-500" />
      <h2 className="mt-3 text-lg font-semibold">Access denied</h2>
      <p className="text-sm text-muted-foreground">You don't have permission to import assets.</p>
    </Card>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Asset Import (one row = one asset)</h1>
          <p className="text-sm text-muted-foreground">CSV upload · Step {step} of 3 · Quantity-based items belong in Spare Inventory.</p>
        </div>
        <Button variant="outline" onClick={() => navigate({ to: "/assets" })}>
          <ArrowLeft className="mr-1 h-4 w-4" /> Back to Assets
        </Button>
      </div>

      <ol className="flex flex-wrap items-center gap-2 text-sm">
        {(["Download Template", "Upload & Validate", "Review & Confirm"] as const).map((label, i) => {
          const n = (i + 1) as 1 | 2 | 3;
          return (
            <li key={label} className={`flex items-center gap-2 rounded-md border px-3 py-1.5 ${step === n ? "border-primary bg-primary/5 text-primary font-medium" : "text-muted-foreground"}`}>
              <span className="grid h-5 w-5 place-items-center rounded-full bg-muted text-xs">{n}</span>
              {label}
            </li>
          );
        })}
      </ol>

      {step === 1 && (
        <Card className="space-y-4 p-6">
          <div>
            <h2 className="font-semibold">Step 1 — Download CSV Template</h2>
            <p className="mt-1 text-sm text-muted-foreground">Fill in your asset rows using the template below, then come back to upload.</p>
          </div>
          <Button onClick={downloadTemplate}><Download className="mr-1 h-4 w-4" />Download CSV Template</Button>

          <Accordion type="single" collapsible className="mt-2">
            <AccordionItem value="cols">
              <AccordionTrigger>Column instructions ({IMPORT_COLUMNS.length})</AccordionTrigger>
              <AccordionContent>
                <div className="space-y-5">
                  <div className="space-y-2">
                    <h3 className="font-medium text-sm text-foreground">Classification & Naming</h3>
                    <ul className="list-disc pl-5 space-y-1.5 text-sm text-muted-foreground">
                      <li><strong className="text-foreground">asset_class</strong> — <span className="text-destructive font-medium">Required</span>. "Asset Type". One of: Equipment, Infrastructure, Vehicle, IT, Furniture, Other.</li>
                      <li><strong className="text-foreground">asset_nature</strong> — <span className="text-destructive font-medium">Required</span>. "Asset Nature". One of: Major, Minor.</li>
                      <li><strong className="text-foreground">parent_category_name</strong> — <span className="text-destructive font-medium">Required</span>. "Category". Must match an existing top-level Category.</li>
                      <li><strong className="text-foreground">asset_name</strong> — <span className="text-destructive font-medium">Required</span>. "Asset Name". Specific name for the asset. Auto-creates under Category if it doesn't already exist.</li>
                      <li><strong className="text-foreground">make, model, serial_number</strong> — <span className="text-destructive font-medium">Required</span>. Hardware metadata fields.</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <h3 className="font-medium text-sm text-foreground">Financial & Vendor Details</h3>
                    <ul className="list-disc pl-5 space-y-1.5 text-sm text-muted-foreground">
                      <li><strong className="text-foreground">basic_cost</strong> — <span className="text-destructive font-medium">Required</span>. Numeric value in ₹.</li>
                      <li><strong className="text-foreground">gst_rate</strong> — Optional. Numeric percentage.</li>
                      <li><strong className="text-foreground">supplier_name</strong> — Optional. Must match an existing vendor in the system.</li>
                      <li><strong className="text-foreground">funding_source</strong> — Optional. {FUNDING_SOURCES.join(" | ")}.</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <h3 className="font-medium text-sm text-foreground">Allocation Details</h3>
                    <ul className="list-disc pl-5 space-y-1.5 text-sm text-muted-foreground">
                      <li><strong className="text-foreground">allocation_type</strong> — <span className="text-destructive font-medium">Required</span>. One of: none, in_store, allocated.</li>
                      <li><strong className="text-foreground">assignment_type</strong> — <span className="text-amber-600 font-medium">Required if allocated</span>. One of: department, internal_user.</li>
                      <li><strong className="text-foreground">store_name</strong> — <span className="text-amber-600 font-medium">Required if in_store</span>. Must match an existing store.</li>
                      <li><strong className="text-foreground">location_name</strong> — <span className="text-amber-600 font-medium">Required if allocated</span>. Must match an existing location.</li>
                      <li><strong className="text-foreground">department_name</strong> — <span className="text-amber-600 font-medium">Required if assigned to department</span>.</li>
                      <li><strong className="text-foreground">custodian_email</strong> — <span className="text-amber-600 font-medium">Required if assigned to internal_user</span>. Must match user's exact email.</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <h3 className="font-medium text-sm text-foreground">Dates & Additional Metadata</h3>
                    <ul className="list-disc pl-5 space-y-1.5 text-sm text-muted-foreground">
                      <li><strong className="text-foreground">criticality</strong> — Optional. {CRITICALITIES.join(" | ")}.</li>
                      <li><strong className="text-foreground">requires_onboarding</strong> — Optional. yes | no.</li>
                      <li><strong className="text-foreground">year_of_manufacture</strong> — Optional. Numeric year (e.g. 2024).</li>
                      <li><strong className="text-foreground">invoice_date / capitalisation_date</strong> — Optional. Format: DD-MM-YYYY.</li>
                      <li><strong className="text-foreground">warranty_start / warranty_end</strong> — Optional. Format: DD-MM-YYYY.</li>
                      <li><strong className="text-foreground">po_number, invoice_number</strong> — Optional text fields.</li>
                    </ul>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <div className="pt-2">
            <Button onClick={() => setStep(2)}>Next: Upload <ArrowRight className="ml-1 h-4 w-4" /></Button>
          </div>
        </Card>
      )}

      {step === 2 && (
        <Card className="space-y-4 p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">Step 2 — Upload &amp; Validate</h2>
            <Button variant="outline" size="sm" onClick={() => setStep(1)}><ArrowLeft className="mr-1 h-4 w-4" />Back</Button>
          </div>

          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
            onClick={() => fileInputRef.current?.click()}
            className="cursor-pointer rounded-lg border-2 border-dashed p-10 text-center hover:bg-muted/40"
          >
            <Upload className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="mt-2 text-sm font-medium">Drop CSV here or click to browse</p>
            <p className="text-xs text-muted-foreground">Accepted: .csv</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
            />
          </div>

          {parsing && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Parsing…</div>}

          {validated.length > 0 && (
            <>
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary">Total: {validated.length}</Badge>
                <Badge className="bg-emerald-500/15 text-emerald-700 border-emerald-500/30" variant="outline">Valid: {validRows.length}</Badge>
                <Badge className="bg-red-500/15 text-red-700 border-red-500/30" variant="outline">Errors: {errorRows.length}</Badge>
                {errorRows.length > 0 && (
                  <Button size="sm" variant="outline" onClick={() => downloadErrorReport(validated)}>
                    <Download className="mr-1 h-4 w-4" />Error Report
                  </Button>
                )}
              </div>

              {errorRows.length > 0 && (
                <div className="max-h-80 overflow-auto rounded-md border">
                  <Table>
                    <TableHeader><TableRow><TableHead>Row</TableHead><TableHead>Asset</TableHead><TableHead>Errors</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {errorRows.map((v) => (
                        <TableRow key={v.rowIndex}>
                          <TableCell className="font-mono text-xs">{v.rowIndex}</TableCell>
                          <TableCell>{v.raw.asset_name || "—"}</TableCell>
                          <TableCell className="text-sm text-destructive">{v.errors.join("; ")}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              <div className="pt-2">
                <Button disabled={validRows.length === 0} onClick={() => setStep(3)}>
                  Next: Review {validRows.length} valid <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </>
          )}
        </Card>
      )}

      {step === 3 && (
        <Card className="space-y-4 p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">Step 3 — Review &amp; Confirm</h2>
            <Button variant="outline" size="sm" onClick={() => setStep(2)} disabled={importing || !!summary}><ArrowLeft className="mr-1 h-4 w-4" />Back</Button>
          </div>

          {!summary ? (
            <>
              <p className="text-sm text-muted-foreground">Preview of first 10 valid rows. {errorRows.length > 0 && `${errorRows.length} error rows will be skipped.`}</p>
              <div className="max-h-80 overflow-auto rounded-md border">
                <Table>
                  <TableHeader><TableRow><TableHead>Row</TableHead><TableHead>Name</TableHead><TableHead>Class</TableHead><TableHead>Category</TableHead><TableHead>Cost</TableHead><TableHead>Store</TableHead><TableHead>Location</TableHead><TableHead>Dept</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {validRows.slice(0, 10).map((v) => (
                      <TableRow key={v.rowIndex}>
                        <TableCell className="font-mono text-xs">{v.rowIndex}</TableCell>
                        <TableCell>{v.raw.asset_name}</TableCell>
                        <TableCell>{v.raw.asset_class}</TableCell>
                        <TableCell>{v.raw.parent_category_name}</TableCell>
                        <TableCell>{v.raw.basic_cost ?? "—"}</TableCell>
                        <TableCell className="text-muted-foreground text-xs">{v.raw.store_name ?? "—"}</TableCell>
                        <TableCell className="text-muted-foreground text-xs">{v.raw.location_name ?? "—"}</TableCell>
                        <TableCell className="text-muted-foreground text-xs">{v.raw.department_name ?? "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <Button onClick={runImport} disabled={importing || validRows.length === 0}>
                {importing ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" />Importing…</> : `Import ${validRows.length} valid assets`}
              </Button>
            </>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 rounded-md border border-emerald-500/30 bg-emerald-50 p-3 text-sm text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200">
                <CheckCircle2 className="h-5 w-5" />
                <span><strong>{summary.imported.length}</strong> assets imported. <strong>{summary.failed + errorRows.length}</strong> skipped.</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {summary.imported.length > 0 && (
                  <Button variant="outline" onClick={() => downloadCodesList(summary.imported)}>
                    <Download className="mr-1 h-4 w-4" />Download asset codes CSV
                  </Button>
                )}
                <Button onClick={() => navigate({ to: "/assets" })}>Back to Asset Registry</Button>
              </div>
              {summary.imported.length > 0 && (
                <div className="max-h-60 overflow-auto rounded-md border p-3 text-xs font-mono">
                  {summary.imported.join("\n")}
                </div>
              )}
            </div>
          )}
        </Card>
      )}

    </div>
  );
}
